import { mapObject } from "@eusoft/webapp-core/utils/Object";
import { toKebabCase } from "@eusoft/webapp-core/utils/String";
import { forModel } from "@eusoft/webapp-jsx";
import { Action, Content, formatText, isCommitable, objectEditor, parseDate, staticItemsSource, Variable, type IContentOptions, type IEditor, type LocalString, type ViewNode } from "@eusoft/webapp-ui";
import { Card } from "../../components/Card";
import { ChartView } from "../../components/ChartView";
import { Field } from "../../components/Field";
import "./ReportViewContent.scss";
import { groupBy, type IGroup } from "@eusoft/webapp-core/utils/Array";
import { localTable } from "@eusoft/webapp-framework";
import { TimeUnit } from "../../entities/Commands";
import { registerComponent, type ComponentStyle } from "@eusoft/webapp-core";
import type { ChartType, DefaultDataPoint, ChartConfiguration, ChartConfigurationCustomTypesPerDataset, TimeUnit as TimeUnitChart} from "chart.js";
import { it } from 'date-fns/locale';

export interface IReportViewArgs {

}

interface IRowFieldInfo<TValue> {
    format?: (value: TValue) => ViewNode
    label?: ViewNode;
}

export type RowFields<T> = {
    [K in keyof T & string]?: IRowFieldInfo<T[K]>

}

export function chartDatePart(part: TimeUnit) {
    return (TimeUnit[part] as string).toLowerCase() as TimeUnitChart;
}

export function formatDatePart(part: TimeUnit, value: Date|string) {

    value = parseDate(value);

    if (!value)
        return "";

    if (part == TimeUnit.Minute)
        return value.toLocaleDateString(localTable.language, {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    if (part == TimeUnit.Hour)
        return value.toLocaleDateString(localTable.language, {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour:"2-digit"
        });
    if (part == TimeUnit.Day)
        return value.toLocaleDateString(localTable.language, {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    if (part == TimeUnit.Month)
        return value.toLocaleDateString(localTable.language, {
            month: "long",
            year: "numeric"
        });
    if (part == TimeUnit.Year)
        return value.toLocaleDateString(localTable.language, {
            year: "numeric"
        });
}

export interface IReportViewOptions<TFilter, TRow> extends IContentOptions<ObjectLike> {

    filterEditor: IEditor<TFilter>;

    createRowView?: (row: TRow) => ViewNode;

    createGroupView?: (row: IGroup<unknown, TRow>) => ViewNode;

    rowHeader?: (row: TRow) => ViewNode;

    groupBy?: (row: TRow) => unknown;

    formatGroupHeader?: (value: unknown) => ViewNode,

    rowFieldLabelPrefix?: string;

    prepareFilter?: (filter: TFilter) => TFilter;

    rowFields?: RowFields<TRow>;

    groupLabel?: ViewNode;

    chartDimension?: keyof TRow;
}

interface IReportChart<
    TType extends ChartType = ChartType,
    TData = DefaultDataPoint<TType>,
    TLabel = unknown> {

    config: ChartConfiguration<TType, TData, TLabel> | ChartConfigurationCustomTypesPerDataset<TType, TData, TLabel>;

    style?: ComponentStyle;
}

export class ReportViewContent<TFilter, TRow> extends Content<IReportViewOptions<TFilter, TRow>, IReportViewArgs> {

    protected _lastFilter: TFilter;

    constructor() {

        super();

        this.init(ReportViewContent, {
            style: ["panel"],
            body: forModel(this, m => <> 
                <Card name="filter" collapsable header={formatText("filter")}>
                    {m.filterEditor}
                    <Action name="clear-filter" style="text" onExecuteAsync={() => m.clearFilter()}>clear-filter</Action>
                </Card>
                <Action name="execute" onExecuteAsync={() => m.executeReportAsync()}>execute</Action>
                <div className="charts-card" visible={m.useCharts}>
                    {objectEditor<this>(bld => <>

                        {bld.singleSelector(a => a.chartDimension, {
                            label: "dimension",
                            validators: [],
                            visible: a => a.useCharts,
                            editor: {
                                itemsSource: staticItemsSource(...Array.from(mapObject(this.rowFields))
                                  .map(a => [this.formatFieldLabel(a.key), a.key] as [LocalString, keyof TRow]))
                            }
                        })}
                    </>, {
                        value: this,
                        commitMode: "auto-inplace",
                        style: "vertical",
                        validationMode: "onInputChange",
                        inputField: { style: "filled" },
                    })}
                    <div visible={m.useCharts} className="chart-list">
                        {m.charts.forEach(a => <ChartView style={a.style} config={a.config} />)}
                    </div>
                </div>
  
                {!m.groupBy ?

                    <>{m.rows.forEach(a => m.createRowView(a))}</>
                    :
                    <>{m.rowGroups.forEach(a => m.createGroupView(a))}</>
                }
            </>)
        });

        this.rows = [];
        this.charts = [];

        this.onChanged("chartDimension", v => this.updateCharts(this._lastFilter));
    }


    protected clearFilter() {
        this.filterEditor.value = {} as TFilter;
    }


    protected async queryAsync(filter: TFilter): Promise<TRow[]> {
        return [];
    }


    override async onOpenAsync() {

        const json = localStorage.getItem(`${this.name}@filter`);
        if (json && this.filterEditor)
            this.filterEditor.value = JSON.parse(json);
    }

    getChartLocale() {
        return localTable.language == "IT" ? it : undefined;
    }

    async executeReportAsync() {

        if (isCommitable(this.filterEditor) && !await this.filterEditor.commitAsync())
            return;

        const filter = this.prepareFilter(this.filterEditor.value);
        localStorage.setItem(`${this.name}@filter`, JSON.stringify(filter));

        this.rows = (await this.queryAsync(filter)) ?? [];

        if (this.groupBy)
            this.rowGroups = groupBy(this.rows, this.groupBy) ?? [];
        else
            this.rowGroups = [];

        this._lastFilter = filter;

        this.useCharts = this.updateCharts(this._lastFilter);
    }


    override async onLoadAsync(args: IReportViewArgs) {

        return true;
    }

    prepareFilter(filter: TFilter) {
        return filter;
    }

    rowHeader(row: TRow) {

    }

    formatFieldValue(value: unknown, info: IRowFieldInfo<unknown>) {


        const res = info.format ? info.format(value) : value?.toString();
        if (typeof res == "string" && res.length == 0)
            return undefined;
        return res;
    }


    formatFieldLabel(label: keyof TRow & string) {

        const info = this.rowFields[label];
        return info.label ?? formatText((this.rowFieldLabelPrefix ?? "") + toKebabCase(label));
    }

    createGroupView(group: IGroup<unknown, TRow>) {

        let body: ViewNode;

        let columns = 1;

        if (this.rowFields) {

            columns = Object.keys(this.rowFields).length + 1;

            const map = Array.from(mapObject(this.rowFields));

            body = <>
                <header>{this.groupLabel}</header>
                {map.forEach(a => <header>{this.formatFieldLabel(a.key)}</header>)}
                {group.values.forEach(r => <>
                    <div className="row-header">{this.rowHeader(r)}</div>
                    {map.forEach(a => <div>{this.formatFieldValue(r[a.key], a.value)}</div>) }
                </>)}
            </>
        }

        return <Card style={["fieldset", "group"]} header={this.formatGroupHeader(group.key)}>
            <div> 
                <Variable inline={false} name="columns" value={columns.toString()} />
                {body}
            </div>
        </Card>;
    }


    createRowView(row: TRow) {

        let body: ViewNode;

        if (this.rowFields) {


            body = <>{Array.from(mapObject(this.rowFields)).forEach(a =>
                <Field label={this.formatFieldLabel(a.key)}>
                    {this.formatFieldValue(row[a.key], a.value)}
                </Field>)}</>
        }

        return <Card style="fieldset" header={this.rowHeader(row)}>
            {body}
        </Card>;
    }

    formatGroupHeader(value: unknown) {

        return value?.toString();
    }

    updateCharts(filter: TFilter) : boolean {
        return false;
    }

    charts: IReportChart<ChartType, unknown, unknown>[];
    
    groupBy?: (row: TRow) => unknown;

    rowFieldLabelPrefix: string;

    rowFields?: RowFields<TRow>;

    filterEditor: IEditor<TFilter>;

    rows: TRow[];

    rowGroups: IGroup<unknown, TRow>[];

    groupLabel: ViewNode;

    activeChart: number = 0;

    chartDimension: keyof TRow & string;

    useCharts: boolean = false;
}

registerComponent(ReportViewContent, "ReportViewContent");